import type {
  SmartRectangleTableConfig,
  SmartRoundTableConfig,
  SmartTableChairModel,
  SmartTableConfig,
  Symbol3DMetadata,
} from "../../../../types/floorplannerTypes";

export const SMART_TABLE_CONFIG_VERSION = 1 as const;
export const SMART_TABLE_MIN_SIZE_M = 0.6;
export const SMART_TABLE_DEFAULT_HEIGHT_M = 0.76;
export const SMART_TABLE_MIN_HEIGHT_M = 0.5;
export const SMART_TABLE_MAX_HEIGHT_M = 1.2;
export const SMART_TABLE_DEFAULT_SEAT_SPACING_M = 0.7;
export const SMART_TABLE_MIN_SEAT_SPACING_M = 0.5;
export const SMART_TABLE_MAX_SEAT_SPACING_M = 2;
export const SMART_TABLE_MAX_MANUAL_CHAIRS_PER_EDGE = 100;
export const SMART_RECT_TABLE_MODEL_KEY = "dining:smart-table-rectangle";
export const SMART_ROUND_TABLE_MODEL_KEY = "dining:smart-table-round";
export const SMART_RECT_TABLE_LIBRARY_KEY = "MANUAL/smart-rectangular-table";
export const SMART_ROUND_TABLE_LIBRARY_KEY = "MANUAL/smart-round-table";

const finiteOr = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const chairModel = (value: unknown): SmartTableChairModel =>
  value === "armchair" ? "armchair" : "chair";

const chairMode = (value: unknown): "automatic" | "manual" =>
  value === "manual" ? "manual" : "automatic";

const chairCount = (value: unknown, fallback = 0): number =>
  clamp(
    Math.round(finiteOr(value, fallback)),
    0,
    SMART_TABLE_MAX_MANUAL_CHAIRS_PER_EDGE,
  );

export function distributeSmartRectangleChairTotal(
  value: number,
  currentCounts: SmartRectangleTableConfig["chairs"]["manualCounts"],
): SmartRectangleTableConfig["chairs"]["manualCounts"] {
  const activeEdges: Array<keyof typeof currentCounts> = ["left", "right"];
  if (currentCounts.start > 0) activeEdges.push("start");
  if (currentCounts.end > 0) activeEdges.push("end");
  const total = clamp(
    Math.round(finiteOr(value, 0)),
    0,
    getSmartRectangleChairTotalCapacity(currentCounts),
  );
  const perEdge = Math.floor(total / activeEdges.length);
  const remainder = total % activeEdges.length;
  const nextCounts = { left: 0, right: 0, start: 0, end: 0 };

  activeEdges.forEach((edge, index) => {
    nextCounts[edge] = perEdge + (index < remainder ? 1 : 0);
  });

  return nextCounts;
}

export function getSmartRectangleChairTotalCapacity(
  currentCounts: SmartRectangleTableConfig["chairs"]["manualCounts"],
): number {
  return (
    2 + Number(currentCounts.start > 0) + Number(currentCounts.end > 0)
  ) * SMART_TABLE_MAX_MANUAL_CHAIRS_PER_EDGE;
}

const seatSpacing = (value: unknown): number =>
  clamp(
    finiteOr(value, SMART_TABLE_DEFAULT_SEAT_SPACING_M),
    SMART_TABLE_MIN_SEAT_SPACING_M,
    SMART_TABLE_MAX_SEAT_SPACING_M,
  );

export function createDefaultSmartRectangleTableConfig(): SmartRectangleTableConfig {
  return {
    version: SMART_TABLE_CONFIG_VERSION,
    shape: "rectangle",
    width: 0.9,
    length: 1.4,
    chairs: {
      enabled: true,
      mode: "automatic",
      model: "chair",
      seatSpacing: SMART_TABLE_DEFAULT_SEAT_SPACING_M,
      automaticEnds: {
        start: false,
        end: false,
      },
      manualCounts: {
        left: 2,
        right: 2,
        start: 0,
        end: 0,
      },
    },
  };
}

export function createDefaultSmartRoundTableConfig(): SmartRoundTableConfig {
  return {
    version: SMART_TABLE_CONFIG_VERSION,
    shape: "round",
    diameter: 1.5,
    support: "pedestal",
    chairs: {
      enabled: true,
      mode: "automatic",
      model: "chair",
      seatSpacing: SMART_TABLE_DEFAULT_SEAT_SPACING_M,
      manualCount: 6,
    },
  };
}

export function createSmartTable3DMetadata(
  shape: SmartTableConfig["shape"],
): Symbol3DMetadata {
  return {
    sourceMode: "primitiveOnly",
    modelKey:
      shape === "round"
        ? SMART_ROUND_TABLE_MODEL_KEY
        : SMART_RECT_TABLE_MODEL_KEY,
    height: SMART_TABLE_DEFAULT_HEIGHT_M,
    baseElevation: 0,
    collision: "box",
  };
}

export function normalizeSmartTableConfig(value: unknown): SmartTableConfig {
  const raw = value && typeof value === "object" ? (value as any) : {};

  if (raw.shape === "round") {
    const defaults = createDefaultSmartRoundTableConfig();
    const spacing = seatSpacing(raw.chairs?.seatSpacing);
    const manualCount = chairCount(
      raw.chairs?.manualCount,
      defaults.chairs.manualCount,
    );
    const enabled = raw.chairs?.enabled !== false;
    const mode = chairMode(raw.chairs?.mode);
    const manualMinimum =
      enabled && mode === "manual"
        ? (manualCount * spacing) / Math.PI
        : SMART_TABLE_MIN_SIZE_M;

    return {
      version: SMART_TABLE_CONFIG_VERSION,
      shape: "round",
      diameter: Math.max(
        finiteOr(raw.diameter, defaults.diameter),
        SMART_TABLE_MIN_SIZE_M,
        manualMinimum,
      ),
      support: raw.support === "separateLegs" ? "separateLegs" : "pedestal",
      chairs: {
        enabled,
        mode,
        model: chairModel(raw.chairs?.model),
        seatSpacing: spacing,
        manualCount,
      },
    };
  }

  const defaults = createDefaultSmartRectangleTableConfig();
  const spacing = seatSpacing(raw.chairs?.seatSpacing);
  const enabled = raw.chairs?.enabled !== false;
  const mode = chairMode(raw.chairs?.mode);
  const manualCounts = {
    left: chairCount(
      raw.chairs?.manualCounts?.left,
      defaults.chairs.manualCounts.left,
    ),
    right: chairCount(
      raw.chairs?.manualCounts?.right,
      defaults.chairs.manualCounts.right,
    ),
    start: chairCount(
      raw.chairs?.manualCounts?.start,
      defaults.chairs.manualCounts.start,
    ),
    end: chairCount(
      raw.chairs?.manualCounts?.end,
      defaults.chairs.manualCounts.end,
    ),
  };
  const minLength =
    enabled && mode === "manual"
      ? Math.max(manualCounts.left, manualCounts.right) * spacing
      : SMART_TABLE_MIN_SIZE_M;
  const minWidth =
    enabled && mode === "manual"
      ? Math.max(manualCounts.start, manualCounts.end) * spacing
      : SMART_TABLE_MIN_SIZE_M;

  return {
    version: SMART_TABLE_CONFIG_VERSION,
    shape: "rectangle",
    width: Math.max(
      finiteOr(raw.width, defaults.width),
      SMART_TABLE_MIN_SIZE_M,
      minWidth,
    ),
    length: Math.max(
      finiteOr(raw.length, defaults.length),
      SMART_TABLE_MIN_SIZE_M,
      minLength,
    ),
    chairs: {
      enabled,
      mode,
      model: chairModel(raw.chairs?.model),
      seatSpacing: spacing,
      automaticEnds: {
        start: raw.chairs?.automaticEnds?.start === true,
        end: raw.chairs?.automaticEnds?.end === true,
      },
      manualCounts,
    },
  };
}

export function normalizeSmartTableHeight(value: unknown): number {
  return clamp(
    finiteOr(value, SMART_TABLE_DEFAULT_HEIGHT_M),
    SMART_TABLE_MIN_HEIGHT_M,
    SMART_TABLE_MAX_HEIGHT_M,
  );
}
